import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useScrollToHash } from '../utils/use-scroll-to-hash';
import { apiUrl } from '../utils/apiBase';
import { authFetch } from '../utils/auth-fetch';
import { useAuth } from '../auth/auth-context';
import { PartnerSearchPanel } from './partner-search-panel';
import { PlayerAutocomplete } from './player-autocomplete';
import { RatingChip, type RatingTag } from './rating-chip';
import { signupUnitCount, signupUnitOf } from './signup-unit';
import { NewPlayerInline } from './new-player-inline';
import TournamentResultsView from '../components/TournamentResultsView';
import RoundScoringPanel from './round-scoring-panel';
import { resolveGroups, type ScoringResponse } from './scoring-types';
import { legacyRoundLinkPath } from './round-link';
import { buildRoundStateMessage, isPublicSignupMode, isSignupStage, signupsStillMatter, waitlistReasonOf, type SignupMode } from './round-state-message';

// Public API may serve sets in either format depending on data source:
//   - legacy tuple [p1, p2] from historical results
//   - new object { p1, p2 } from LeagueMatchResult records
// We accept both and normalise via `setScore()`.
type MatchSetRaw = [number, number] | { p1: number; p2: number };

function setScore(set: MatchSetRaw): string {
  if (Array.isArray(set)) {
    return `${set[0]}:${set[1]}`;
  }

  return `${set.p1}:${set.p2}`;
}

interface SidePlayer {
  name: string;
  ratingBefore: number | null;
}

interface MatchItem {
  id: number;
  status: string;
  player1Name: string;
  player2Name: string;
  sets: MatchSetRaw[];
  winnerSide: string | null;
  ratingBefore1: number | null;
  ratingBefore2: number | null;
  side1?: SidePlayer[];
  side2?: SidePlayer[];
}

interface RoundMeta {
  startTime: string | null;
  venueOverride: string | null;
  venueNotes: string | null;
}

interface LeagueVenueMeta {
  venue: string | null;
  venueCity: string | null;
  defaultStartTime: string | null;
}

interface RoundParticipant {
  id: string;
  leaguePlayerId: number | null;
  partnerPlayerId?: number | null;
  name: string;
  singlesRating: number | null;
  doublesRating: number | null;
  status: string;
  waitlistReason?: string | null;
  signedAt: string;
  groupName: string | null;
}

interface MatchesResponse {
  matches: MatchItem[];
  embargoed?: boolean;
  round?: RoundMeta;
}

interface RoundSeats {
  maxParticipants: number | null;
  signedUpCount: number;
  freeSeats: number | null;
  isFull: boolean;
  isOverLimit: boolean;
}

interface ParticipantsResponse {
  items: RoundParticipant[];
  seats?: RoundSeats;
}

interface RoundShape {
  id: number;
  name: string;
  status: string;
  seasonId?: number | null;
  signupMode?: SignupMode | null;
  startDate: string | null;
  signupOpenAt: string | null;
  signupCloseAt: string | null;
  rulesOverrideJson?: string | null;
  // Turnout the organiser planned this kolejka for. Informational — signing up is
  // never refused because the number was not reached.
  minParticipants?: number | null;
}

interface LeagueSeasonItem {
  id: number;
  name: string;
  number: number;
}

interface LeagueShape {
  name: string;
  discipline?: string | null;
  playMode?: string | null;
  venue?: string | null;
  venueCity?: string | null;
  defaultStartTime?: string | null;
  defaultRulesJson?: string | null;
  signupMode?: SignupMode | null;
  requireRoundSignup?: boolean;
  rounds: RoundShape[];
  seasons?: LeagueSeasonItem[];
}

function computeBestOf(leagueRulesJson: string | null, roundRulesJson: string | null): number {
  let base: number | undefined;
  let override: number | undefined;
  try {
    base = leagueRulesJson ? (JSON.parse(leagueRulesJson) as { bestOf?: number }).bestOf : undefined;
  } catch {
    base = undefined;
  }
  try {
    override = roundRulesJson ? (JSON.parse(roundRulesJson) as { bestOf?: number }).bestOf : undefined;
  } catch {
    override = undefined;
  }
  return override ?? base ?? 3;
}

interface CanSignupResponse {
  canSignup: boolean;
  requiresApproval?: boolean;
  defaultPlayerId?: number;
  defaultPlayer?: { id: number; firstName: string; lastName: string } | null;
  mySignupStatus?: 'SIGNED_UP' | 'DECLINED' | 'WAITLIST' | null;
  reason?: string;
}

interface PlayerSuggestion {
  id: number;
  firstName: string;
  lastName: string;
}

// A guest off an invite link lands on the list at once when the base already knows the
// person; everyone else still waits for the organiser's decision.
interface GuestOutcome {
  name: string;
  status: 'SIGNED_UP' | 'WAITLIST' | 'PENDING';
  isNewPlayer: boolean;
}

function guestOutcomeMessage(outcome: GuestOutcome): string {
  if (outcome.status === 'SIGNED_UP') {
    return `Zapisano na kolejkę: ${outcome.name}`;
  }

  if (outcome.status === 'WAITLIST') {
    return `${outcome.name} — lista rezerwowa, limit miejsc jest pełny`;
  }

  return outcome.isNewPlayer
    ? 'Twoja prośba o dodanie czeka na zatwierdzenie'
    : 'Twój zapis czeka na zatwierdzenie';
}

interface SelfSignupResponse {
  status?: 'SIGNED_UP' | 'WAITLIST';
  waitlistReason?: 'APPROVAL_REQUIRED' | 'LIMIT_REACHED' | null;
}

// The two reasons for landing on the waitlist read completely differently to the
// person signing up: one waits for a decision, the other waits for a free seat.
function selfSignupMessage(response: SelfSignupResponse, okMessage: string): string {
  if (response.status !== 'WAITLIST') {
    return okMessage;
  }

  if (response.waitlistReason === 'LIMIT_REACHED') {
    return 'Brak wolnych miejsc — jesteś na liście oczekujących.';
  }

  return 'Zapis przyjęty — czeka na decyzję organizatora.';
}

const GUEST_SIGNUP_ERROR: Record<string, string> = {
  SIGNUP_NOT_PUBLIC: 'Kod z linku nie został przyjęty — poproś organizatora o aktualny link.',
  ROUND_NOT_OPEN: 'Zapisy na tę kolejkę są już zamknięte.',
  RATE_LIMIT_EXCEEDED: 'Za dużo prób — odczekaj chwilę i spróbuj ponownie.',
  PLAYER_NOT_FOUND: 'Nie znaleziono tego zawodnika.',
  ALREADY_SIGNED_UP: 'Ta osoba jest już zapisana na tę kolejkę.',
  ALREADY_PENDING: 'Zgłoszenie tej osoby już czeka na decyzję organizatora.',
  PARTNER_ALREADY_SIGNED_UP: 'Partner jest już zapisany na tę kolejkę.',
};

// The API answers a rejected signup with a code, not a sentence — showing the raw
// body would put JSON in front of the player.
async function signupErrorMessage(response: Response): Promise<string> {
  const text = await response.text();
  try {
    const code = (JSON.parse(text) as { error?: string }).error ?? '';

    return GUEST_SIGNUP_ERROR[code] ?? (code || 'Nie udało się zapisać.');
  } catch {
    return text || 'Nie udało się zapisać.';
  }
}

// An americano league is stored as SINGLES so that signups stay individual —
// without saying it out loud, a player reads "singiel" and expects 1 vs 1.
function AmericanoSignupNote() {
  return (
    <p className="round-signup-cta__hint round-signup-cta__hint--mode" data-testid="americano-signup-note">
      <strong>Americano:</strong> zapisujesz się sam, bez partnera — ale gra się w deblu 2 na 2.
      Pary układa organizator na każdą rundę osobno, więc partnerzy zmieniają się w trakcie
      wieczoru.
    </p>
  );
}

function formatMatchSets(sets: MatchSetRaw[], winnerSide: string | null): string {
  if (sets.length === 0) {
    return winnerSide && winnerSide.startsWith('WALKOVER') ? 'wo' : '—';
  }

  return sets.map(setScore).join(', ');
}

function participantInitials(name: string): string {
  // For pair names "Foo Bar / Baz Qux" take first letter of each player's first name.
  if (name.includes('/')) {
    const [a, b] = name.split('/').map((s) => s.trim());
    const ia = (a.charAt(0) || '?').toUpperCase();
    const ib = (b.charAt(0) || '').toUpperCase();
    return ia + ib;
  }
  const [first = '', last = ''] = name.split(/\s+/);
  return ((first.charAt(0) || '?') + (last.charAt(0) || '')).toUpperCase();
}

function splitDisplayName(displayName: string | null | undefined): { firstName: string; lastName: string } {
  const [firstName = '', ...rest] = (displayName ?? '').trim().split(/\s+/);

  return { firstName, lastName: rest.join(' ') };
}

const DOUBLES_DISCIPLINES = ['DOUBLES', 'DOUBLES_M', 'DOUBLES_F', 'MIXED'];

function isDoublesDiscipline(discipline: string | null): boolean {
  return discipline != null && DOUBLES_DISCIPLINES.includes(discipline);
}

// The tag drives the value: an americano league is stored as SINGLES but seeds and
// scores on the doubles rating, so reading the discipline again here would show a
// singles number under a "D" chip.
function ratingForTag(participant: RoundParticipant, tag: RatingTag): number | null {
  return tag === 'D' ? participant.doublesRating : participant.singlesRating;
}

function WaitingGroup(props: {
  testId: string;
  countTestId: string;
  title: string;
  hint: string;
  participants: RoundParticipant[];
  emptyText: string;
  relevant: boolean;
  ratingTag: RatingTag;
}) {
  const isEmpty = props.participants.length === 0;

  if (isEmpty && !props.relevant) {
    return null;
  }

  return (
    <div className="round-signup-panel__group" data-testid={props.testId}>
      <h3 className="round-signup-panel__group-title round-signup-panel__group-title--warn">
        {props.title}
        <span className="round-signup-panel__count round-signup-panel__count--warn" data-testid={props.countTestId}>
          {props.participants.length}
        </span>
      </h3>
      <p className="round-signup-panel__group-hint">{props.hint}</p>
      {isEmpty ? (
        <p className="round-signup-panel__empty">{props.emptyText}</p>
      ) : (
        <ul className="round-signup-panel__list">
          {props.participants.map((participant) => (
            <li key={participant.id} className="round-signup-panel__row round-signup-panel__row--pending">
              <span className="round-signup-panel__monogram" aria-hidden="true">
                {participantInitials(participant.name)}
              </span>
              <span className="round-signup-panel__name">{participant.name}</span>
              <span className="round-signup-panel__rating">
                <RatingChip
                  tag={props.ratingTag}
                  value={ratingForTag(participant, props.ratingTag)}
                  pair={participant.partnerPlayerId != null}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatStartTime(value: string | null): string | null {
  if (!value) {
    return null;
  }

  // Accept either ISO datetime or plain HH:MM.
  if (/^\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }

  try {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    }
  } catch {
    // fall through
  }

  return value;
}

// The round link page renders this view itself, so ids and the invite code can come
// in as props instead of the URL.
interface RoundDetailProps {
  leagueId?: string;
  roundId?: string;
  accessCode?: string | null;
}

const RoundDetail: React.FC<RoundDetailProps> = (props) => {
  const params = useParams<{ id: string; roundId: string }>();
  const id = props.leagueId ?? params.id;
  const roundId = props.roundId ?? params.roundId;
  const embeddedInRoundLink = props.roundId !== undefined;
  const { user } = useAuth();
  const [leagueName, setLeagueName] = useState('');
  const [roundName, setRoundName] = useState('');
  const [leagueMeta, setLeagueMeta] = useState<LeagueVenueMeta | null>(null);
  const [roundMeta, setRoundMeta] = useState<RoundMeta | null>(null);
  const [signupMode, setSignupMode] = useState<SignupMode | null>(null);
  // `?kod=` off the organiser's invite link — orthogonal to signupMode, it lets the
  // holder sign up even into a PRIVATE league.
  const [searchParams] = useSearchParams();
  const accessCode = embeddedInRoundLink ? props.accessCode ?? null : searchParams.get('kod');
  const [requireRoundSignup, setRequireRoundSignup] = useState(false);
  const [roundStatus, setRoundStatus] = useState<string | null>(null);
  const navigate = useNavigate();
  const [roundSeason, setRoundSeason] = useState<LeagueSeasonItem | null>(null);
  const [roundSignupOpenAt, setRoundSignupOpenAt] = useState<string | null>(null);
  const [roundStartDate, setRoundStartDate] = useState<string | null>(null);
  const [roundSignupCloseAt, setRoundSignupCloseAt] = useState<string | null>(null);
  const [roundMinParticipants, setRoundMinParticipants] = useState<number | null>(null);
  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [scoring, setScoring] = useState<ScoringResponse | null>(null);
  const [participants, setParticipants] = useState<RoundParticipant[] | null>(null);
  const [seats, setSeats] = useState<RoundSeats | null>(null);
  const [participantsError, setParticipantsError] = useState(false);
  const [leagueDiscipline, setLeagueDiscipline] = useState<string | null>(null);
  const [americano, setAmericano] = useState(false);
  // Americano plays 2 na 2 on a league stored as SINGLES, so what counts here is
  // everyone's doubles rating — the same one the organiser seeds the rounds on.
  const ratingDiscipline = americano ? 'DOUBLES' : leagueDiscipline;
  const [embargoed, setEmbargoed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  // When the round is backed by ingested tournaments (Bieruń/LABA/...) we
  // can render the rich tournament view (group standings + rating progression)
  // inline. A single "kolejka" can span multiple groups (LIGA I, LIGA II…), so
  // we keep a list and render one TournamentResultsView per group.
  const [tournamentGroups, setTournamentGroups] = useState<
    { tournamentNumber: number; groupName: string | null }[]
  >([]);

  const [canSignup, setCanSignup] = useState<CanSignupResponse | null>(null);
  const [signupQuery, setSignupQuery] = useState('');
  const [signupSuggestions, setSignupSuggestions] = useState<PlayerSuggestion[]>([]);
  const [signupSelected, setSignupSelected] = useState<PlayerSuggestion | null>(null);
  // Doubles signup: 2 player slots (player1 = me, player2 = partner). Each slot
  // is either the selected PlayerSuggestion or null.
  const [pairSlot1, setPairSlot1] = useState<PlayerSuggestion | null>(null);
  const [pairSlot2, setPairSlot2] = useState<PlayerSuggestion | null>(null);
  const [pairQuery, setPairQuery] = useState('');
  const [pairSuggestions, setPairSuggestions] = useState<PlayerSuggestion[]>([]);
  const [pairActiveSlot, setPairActiveSlot] = useState<1 | 2>(1);
  const [signedSortMode, setSignedSortMode] = useState<'rating-desc' | 'name-asc'>('rating-desc');
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupMessage, setSignupMessage] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const signupDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Guest signup form state
  const [guestPlayer, setGuestPlayer] = useState<{ id: number; name: string } | { newPlayer: true; name: string } | null>(null);
  const [guestPartnerName, setGuestPartnerName] = useState('');
  const [guestPartnerId, setGuestPartnerId] = useState<number | null>(null);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [guestOutcome, setGuestOutcome] = useState<GuestOutcome | null>(null);
  const [guestSignedUp, setGuestSignedUp] = useState<string[]>([]);
  const [guestContactError, setGuestContactError] = useState<string | null>(null);

  // Match result editing state
  const [leagueRulesJson, setLeagueRulesJson] = useState<string | null>(null);
  const [roundRulesJson, setRoundRulesJson] = useState<string | null>(null);

  const fetchMatches = useCallback(async () => {
    if (!id || !roundId) {
      return;
    }

    const res = await fetch(apiUrl(`/api/public/leagues/${id}/rounds/${roundId}/matches`));
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = (await res.json()) as MatchesResponse;
    setMatches(data.matches ?? []);
    setEmbargoed(!!data.embargoed);
    setRoundMeta(data.round ?? null);
  }, [id, roundId]);

  const fetchScoring = useCallback(async () => {
    if (!id || !roundId) {
      return;
    }

    try {
      const res = await fetch(apiUrl(`/api/public/leagues/${id}/rounds/${roundId}/scoring`));

      if (!res.ok) {
        setScoring(null);

        return;
      }

      setScoring(await res.json() as ScoringResponse);
    } catch {
      setScoring(null);
    }
  }, [id, roundId]);

  const fetchParticipants = useCallback(async () => {
    if (!id || !roundId) {
      return;
    }

    try {
      const res = await fetch(apiUrl(`/api/public/leagues/${id}/rounds/${roundId}/participants`));
      if (!res.ok) {
        setParticipants(null);
        setParticipantsError(true);

        return;
      }

      const data = (await res.json()) as ParticipantsResponse;
      setParticipants(data.items ?? []);
      setSeats(data.seats ?? null);
      setParticipantsError(false);
    } catch {
      setParticipants(null);
      setParticipantsError(true);
    }
  }, [id, roundId]);

  const fetchLeague = useCallback(async () => {
    if (!id) {
      return;
    }

    const res = await fetch(apiUrl(`/api/public/leagues/${id}`));
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = (await res.json()) as LeagueShape;
    setLeagueName(data.name);
    setLeagueDiscipline(data.discipline ?? null);
    setAmericano(data.playMode === 'AMERICANO');
    setLeagueMeta({
      venue: data.venue ?? null,
      venueCity: data.venueCity ?? null,
      defaultStartTime: data.defaultStartTime ?? null,
    });
    setRequireRoundSignup(!!data.requireRoundSignup);
    const round = (data.rounds ?? []).find((r) => String(r.id) === roundId);
    // The kolejka may override the league's signup mode — the API already resolved it.
    setSignupMode(round?.signupMode ?? data.signupMode ?? null);
    setRoundName(round ? round.name : `Kolejka ${roundId}`);
    setRoundStatus(round?.status ?? null);
    setRoundSeason(
      (data.seasons ?? []).find((season) => season.id === round?.seasonId) ?? null,
    );
    setRoundSignupOpenAt(round?.signupOpenAt ?? null);
    setRoundSignupCloseAt(round?.signupCloseAt ?? null);
    setRoundStartDate(round?.startDate ?? null);
    setRoundMinParticipants(round?.minParticipants ?? null);
    setLeagueRulesJson(data.defaultRulesJson ?? null);
    setRoundRulesJson(round?.rulesOverrideJson ?? null);
  }, [id, roundId]);

  useEffect(() => {
    if (!id || !roundId) {
      return;
    }

    setLoading(true);
    setError(null);

    Promise.all([fetchLeague(), fetchMatches(), fetchParticipants(), fetchScoring()])
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setError('Nie udało się załadować kolejki.');
        setLoading(false);
      });
  }, [id, roundId, fetchLeague, fetchMatches, fetchParticipants, fetchScoring, retryKey]);

  // Discover the tournament projection backing this round (if any). Lets
  // closed Bieruń/LABA rounds render the group standings + rating progression.
  useEffect(() => {
    if (!roundId) {
      return;
    }

    let cancelled = false;
    fetch(apiUrl(`/api/public/league-rounds/${roundId}/tournament`))
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) {
          return;
        }

        const list: { tournamentNumber: number; groupName: string | null }[] = Array.isArray(data.tournaments)
          ? data.tournaments
              .filter((t: { tournamentNumber?: number }) => typeof t.tournamentNumber === 'number')
              .map((t: { tournamentNumber: number; groupName?: string | null }) => ({
                tournamentNumber: Number(t.tournamentNumber),
                groupName: t.groupName ?? null,
              }))
          : data.tournamentNumber
            ? [{ tournamentNumber: Number(data.tournamentNumber), groupName: null }]
            : [];

        setTournamentGroups(list);
      })
      .catch(() => {
        // No projection → fall back to the existing match list rendering.
      });

    return () => {
      cancelled = true;
    };
  }, [roundId]);

  // fetchMatches + fetchLeague always run; fetchParticipants is skipped while embargoed.
  useEffect(() => {
    const handle = setInterval(() => {
      fetchMatches().catch(() => {});
      fetchLeague().catch(() => {});
      if (!embargoed) {
        fetchParticipants().catch(() => {});
        fetchScoring().catch(() => {});
      }
    }, 30000);

    return () => {
      clearInterval(handle);
    };
  }, [embargoed, fetchMatches, fetchParticipants, fetchLeague, fetchScoring]);

  const legacyRedirect = embeddedInRoundLink ? null : legacyRoundLinkPath(accessCode, roundId);
  useEffect(() => {
    if (legacyRedirect) {
      navigate(legacyRedirect, { replace: true });
    }
  }, [legacyRedirect, navigate]);

  // Can-signup probe + default player.
  useEffect(() => {
    if (!user || !id || !roundId) {
      setCanSignup(null);
      return;
    }

    let cancelled = false;
    const canSignupUrl = accessCode
      ? `/api/me/leagues/${id}/rounds/${roundId}/can-signup?code=${encodeURIComponent(accessCode)}`
      : `/api/me/leagues/${id}/rounds/${roundId}/can-signup`;
    authFetch(canSignupUrl)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: CanSignupResponse | null) => {
        if (cancelled) {
          return;
        }

        setCanSignup(data);
        // Prefill autocomplete from defaultPlayer returned by the API (avoids
        // a second fetch + fuzzy-match guesswork).
        if (data?.canSignup && data.defaultPlayer) {
          setSignupSelected({
            id: data.defaultPlayer.id,
            firstName: data.defaultPlayer.firstName,
            lastName: data.defaultPlayer.lastName,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanSignup(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, id, roundId, accessCode]);

  useEffect(() => {
    if (signupSelected) {
      return;
    }

    if (signupDebounceRef.current) {
      clearTimeout(signupDebounceRef.current);
    }

    if (signupQuery.trim().length < 2) {
      setSignupSuggestions([]);
      return;
    }

    signupDebounceRef.current = setTimeout(() => {
      fetch(apiUrl(`/api/players?search=${encodeURIComponent(signupQuery.trim())}`))
        .then((res) => (res.ok ? res.json() : []))
        .then((data: PlayerSuggestion[] | { items: PlayerSuggestion[] }) => {
          const items = Array.isArray(data) ? data : data.items ?? [];
          setSignupSuggestions(items.slice(0, 8));
        })
        .catch(() => {
          setSignupSuggestions([]);
        });
    }, 250);

    return () => {
      if (signupDebounceRef.current) {
        clearTimeout(signupDebounceRef.current);
      }
    };
  }, [signupQuery, signupSelected]);

  // Pair-mode debounce — searches players for whichever slot is active.
  useEffect(() => {
    const trimmed = pairQuery.trim();
    if (trimmed.length < 2) {
      setPairSuggestions([]);
      return;
    }
    const handle = setTimeout(() => {
      fetch(apiUrl(`/api/players?search=${encodeURIComponent(trimmed)}`))
        .then((res) => (res.ok ? res.json() : []))
        .then((data: PlayerSuggestion[] | { items: PlayerSuggestion[] }) => {
          const items = Array.isArray(data) ? data : data.items ?? [];
          setPairSuggestions(items.slice(0, 8));
        })
        .catch(() => setPairSuggestions([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [pairQuery]);

  async function handleSignupSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!signupSelected || !id || !roundId) {
      return;
    }

    setSignupSubmitting(true);
    setSignupError(null);
    setSignupMessage(null);

    try {
      const res = await authFetch(`/api/me/leagues/${id}/rounds/${roundId}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId: signupSelected.id, code: accessCode ?? undefined }),
      });

      if (!res.ok) {
        const text = await res.text();
        setSignupError(text || 'Nie udało się zapisać.');
        setSignupSubmitting(false);

        return;
      }

      const data = (await res.json()) as SelfSignupResponse;
      setSignupMessage(selfSignupMessage(data, 'Zapis przyjęty.'));
      await fetchParticipants();
      setCanSignup((prev) =>
        prev
          ? { ...prev, mySignupStatus: data.status ?? 'SIGNED_UP' }
          : { canSignup: false, mySignupStatus: data.status ?? 'SIGNED_UP' },
      );
    } catch {
      setSignupError('Błąd sieci.');
    } finally {
      setSignupSubmitting(false);
    }
  }

  async function handlePairSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pairSlot1 || !pairSlot2 || pairSlot1.id === pairSlot2.id || !id || !roundId) {
      return;
    }

    setSignupSubmitting(true);
    setSignupError(null);
    setSignupMessage(null);

    try {
      const res = await authFetch(`/api/me/leagues/${id}/rounds/${roundId}/signup-pair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player1Id: pairSlot1.id,
          player2Id: pairSlot2.id,
          code: accessCode ?? undefined,
        }),
      });

      if (!res.ok) {
        setSignupError(await signupErrorMessage(res));
        setSignupSubmitting(false);

        return;
      }

      const data = (await res.json()) as SelfSignupResponse;
      setSignupMessage(selfSignupMessage(data, 'Para zapisana.'));
      setPairSlot1(null);
      setPairSlot2(null);
      setPairQuery('');
      setPairSuggestions([]);
      setPairActiveSlot(1);
      await fetchParticipants();
      setCanSignup((prev) =>
        prev
          ? { ...prev, mySignupStatus: data.status ?? 'SIGNED_UP' }
          : { canSignup: false, mySignupStatus: data.status ?? 'SIGNED_UP' },
      );
    } catch {
      setSignupError('Błąd sieci.');
    } finally {
      setSignupSubmitting(false);
    }
  }

  async function handleWithdraw() {
    if (!id || !roundId) {
      return;
    }

    setWithdrawing(true);
    setSignupError(null);
    setSignupMessage(null);

    try {
      const res = await authFetch(`/api/me/leagues/${id}/rounds/${roundId}/signup`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const text = await res.text();
        setSignupError(text || 'Nie udało się wycofać.');
        setWithdrawing(false);

        return;
      }

      setSignupMessage('Wycofano z kolejki — status zmieniony na "Nie przyjdę".');
      await fetchParticipants();
      setCanSignup((prev) =>
        prev ? { ...prev, mySignupStatus: 'DECLINED' } : { canSignup: false, mySignupStatus: 'DECLINED' },
      );
    } catch {
      setSignupError('Błąd sieci.');
    } finally {
      setWithdrawing(false);
    }
  }

  async function fetchPublicPlayers(query: string) {
    if (!id) {
      return [];
    }

    try {
      const res = await fetch(apiUrl(`/api/public/leagues/${id}/players?q=${encodeURIComponent(query)}`));
      if (!res.ok) {
        return [];
      }

      const data = await res.json() as {
        items?: Array<{
          id: number;
          displayName: string;
          singlesRating?: number | null;
          doublesRating?: number | null;
        }>;
      };

      return (data.items ?? []).map((p) => ({
        id: p.id,
        ...splitDisplayName(p.displayName),
        singlesRating: p.singlesRating ?? null,
        doublesRating: p.doublesRating ?? null,
      }));
    } catch {
      return [];
    }
  }

  async function handleGuestSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setGuestContactError(null);

    if (!guestPlayer) {
      setGuestContactError('Wybierz zawodnika z listy lub utwórz nowego');

      return;
    }

    const seatsAtOnce = Boolean(accessCode) || (signupMode === 'OPEN' && 'id' in guestPlayer);
    if (!seatsAtOnce && !guestEmail.trim() && !guestPhone.trim()) {
      setGuestContactError('Podaj email lub telefon');

      return;
    }

    const isDoublesLeague = isDoublesDiscipline(leagueDiscipline);

    if (isDoublesLeague && !guestPartnerName.trim()) {
      setGuestContactError('Podaj imię i nazwisko partnera');

      return;
    }

    setGuestSubmitting(true);
    try {
      const body: Record<string, unknown> = 'id' in guestPlayer
        ? { playerId: guestPlayer.id }
        : { playerName: guestPlayer.name };

      if (isDoublesLeague && guestPartnerName.trim()) {
        body.partnerPlayerName = guestPartnerName.trim();
      }
      if (isDoublesLeague && guestPartnerId !== null) {
        body.partnerPlayerId = guestPartnerId;
      }
      if (guestEmail.trim()) {
        body.email = guestEmail.trim();
      }
      if (guestPhone.trim()) {
        body.phone = guestPhone.trim();
      }
      if (accessCode) {
        body.code = accessCode;
      }
      const res = await fetch(apiUrl(`/api/public/leagues/${id}/rounds/${roundId}/signup`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        setGuestContactError(GUEST_SIGNUP_ERROR[data.error ?? ''] ?? data.error ?? 'Błąd zapisu');

        return;
      }

      const data = await res.json() as { status?: 'SIGNED_UP' | 'WAITLIST'; playerName?: string };
      const outcome: GuestOutcome = {
        name: data.playerName ?? guestPlayer.name,
        status: data.status ?? 'PENDING',
        isNewPlayer: !('id' in guestPlayer),
      };

      setGuestOutcome(outcome);
      if (outcome.status !== 'PENDING') {
        setGuestSignedUp((names) => [...names, outcome.name]);
        await fetchParticipants();
      }
    } catch {
      setGuestContactError('Błąd połączenia');
    } finally {
      setGuestSubmitting(false);
    }
  }

  function startAnotherGuestSignup(): void {
    setGuestOutcome(null);
    setGuestPlayer(null);
    setGuestPartnerName('');
    setGuestPartnerId(null);
    setGuestEmail('');
    setGuestPhone('');
    setGuestContactError(null);
  }

  useScrollToHash(!loading && !error);

  if (loading) {
    return <div className="event-detail-loading">Ładowanie...</div>;
  }

  if (error) {
    return (
      <div className="event-detail-error">
        <p>{error}</p>
        <button
          type="button"
          className="btn-secondary"
          data-testid="round-error-retry"
          onClick={() => setRetryKey((k) => k + 1)}
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  const ROUND_STATUS_LABEL: Record<string, string> = {
    DRAFT: 'W przygotowaniu',
    REGISTRATION: 'Zapisy otwarte',
    GROUPING: 'Losowanie grup',
    ACTIVE: 'W toku',
    CLOSED: 'Zakończona',
    CANCELLED: 'Odwołana',
  };

  const SIGNUP_MODE_LABEL: Record<SignupMode, string> = {
    PRIVATE: 'Zapisy prywatne',
    OPEN: 'Zapisy otwarte',
    APPROVAL: 'Zapisy z akceptacją',
    APPROVAL_EXCEPT_MEMBERS: 'Zapisy z akceptacją',
  };

  function buildSignupBlockedReason(): { title: string; body: string; cta?: { to: string; label: string } } | null {
    if (canSignup?.canSignup) {
      return null;
    }

    const signupProbeAnswered = canSignup !== null;

    if (signupMode === 'PRIVATE') {
      if (accessCode && !signupProbeAnswered) {
        return null;
      }

      return {
        title: 'Zapisy prywatne',
        body: accessCode
          ? 'Kod z linku nie został przyjęty — poproś organizatora o aktualny link.'
          : 'Tę ligę prowadzi organizator — żeby dołączyć, skontaktuj się z nim bezpośrednio.',
      };
    }
    if (!user && isPublicSignupMode(signupMode)) {
      // While signups are open the guest CTA already offers a working signup path,
      // so the login-only aside would just be a contradictory dead end.
      if (isSignupStage(roundStatus)) {
        return null;
      }

      return {
        title: 'Zapisy otwarte',
        body: 'Zaloguj się, aby się zapisać.',
        cta: { to: '/login', label: 'Zaloguj się →' },
      };
    }
    if (canSignup && !canSignup.canSignup && canSignup.reason) {
      return {
        title: 'Zapis niedostępny',
        body: canSignup.reason,
      };
    }

    return null;
  }

  // A round that is under way or finished can no longer be joined, so every
  // signup-mode hint is noise there.
  const signupInfoRelevant = signupsStillMatter(roundStatus);
  const signupBlocked = signupInfoRelevant ? buildSignupBlockedReason() : null;
  const guestIsNewPlayer = guestPlayer !== null && 'newPlayer' in guestPlayer;
  const inviteCodeActive = Boolean(accessCode) && isSignupStage(roundStatus);
  // Contact is the organiser's way of reaching someone in the decision queue — a person
  // the base already knows is seated at once in an OPEN kolejka and never lands there.
  const guestContactOptional = inviteCodeActive || (signupMode === 'OPEN' && !guestIsNewPlayer);
  const roundStateMessage = buildRoundStateMessage(roundStatus, signupMode, inviteCodeActive);

  const effectiveStartTime = roundMeta?.startTime ?? leagueMeta?.defaultStartTime ?? null;
  const venueIsOverride = !!roundMeta?.venueOverride;
  const effectiveVenue = roundMeta?.venueOverride ?? leagueMeta?.venue ?? null;
  const venueWithCity = effectiveVenue
    ? leagueMeta?.venueCity && !venueIsOverride
      ? `${effectiveVenue}, ${leagueMeta.venueCity}`
      : effectiveVenue
    : null;

  const scoringGroups = scoring ? resolveGroups(scoring).filter((group) => group.matchCount > 0) : [];
  // Pairings stay hidden until the round goes live — same rule the plain match list follows.
  // Once played, the round is read through the same tournament view every other
  // result page uses; this live panel only fills in while there is no tournament yet.
  // Americano is the exception: pairs reshuffle every run, so the tournament view's
  // per-pair table says nothing. Its classification is per player — the very table the
  // scoring page shows — so a closed americano round keeps this panel.
  const showScoringPanel =
    !embargoed &&
    scoringGroups.length > 0 &&
    (roundStatus === 'ACTIVE' ||
      (roundStatus === 'CLOSED' && (americano || tournamentGroups.length === 0)));

  const visibleParticipants = (participants ?? []).filter((p) => p.status !== 'DECLINED');
  const signedParticipants = visibleParticipants.filter((p) => p.status === 'SIGNED_UP');
  const waitlistParticipants = visibleParticipants.filter((p) => p.status === 'WAITLIST');
  const seatsFull = seats?.maxParticipants != null && seats.isFull;
  const roundHasSeatLimit = seats?.maxParticipants != null;
  const roundNeedsApproval = signupMode === 'APPROVAL' || signupMode === 'APPROVAL_EXCEPT_MEMBERS';
  const approvalParticipants = waitlistParticipants.filter(
    (p) => waitlistReasonOf(p.waitlistReason, seatsFull) === 'APPROVAL_REQUIRED'
  );
  const seatQueueParticipants = waitlistParticipants.filter(
    (p) => waitlistReasonOf(p.waitlistReason, seatsFull) === 'LIMIT_REACHED'
  );
  const groupedParticipants = new Map<string, RoundParticipant[]>();
  for (const p of signedParticipants) {
    const key = p.groupName ?? '__nogroup__';
    if (!groupedParticipants.has(key)) {
      groupedParticipants.set(key, []);
    }

    groupedParticipants.get(key)!.push(p);
  }

  return (
    <div className="event-detail event-detail--round">
      <div className="round-back-nav">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="event-detail-back round-back-btn"
        >
          ← Wstecz
        </button>
      </div>
      <h1 className="event-detail-name">
        {leagueName} — {roundName}
      </h1>

      <div className="round-meta">
        {roundStatus && (
          <span
            className={`round-meta__chip round-meta__chip--status round-meta__chip--status-${roundStatus.toLowerCase()}`}
            data-testid="round-status-chip"
          >
            {ROUND_STATUS_LABEL[roundStatus] ?? roundStatus}
          </span>
        )}
        {signupInfoRelevant && signupMode && signupMode !== 'OPEN' && !inviteCodeActive && (
          <span
            className={`round-meta__chip round-meta__chip--mode round-meta__chip--mode-${signupMode.toLowerCase()}`}
            data-testid="round-signup-mode-chip"
          >
            {SIGNUP_MODE_LABEL[signupMode]}
          </span>
        )}
        {signupInfoRelevant && requireRoundSignup && (
          <span className="round-meta__chip round-meta__chip--info" data-testid="round-signup-required-chip">
            Zapisy per kolejka
          </span>
        )}
        {signupInfoRelevant && roundMinParticipants != null && (
          <span className="round-meta__chip" data-testid="round-min-note">
            👥 minimum {signupUnitCount(signupUnitOf(leagueDiscipline), roundMinParticipants)}
          </span>
        )}
        {roundStartDate && (
          <span className="round-meta__chip">
            📅 {new Date(roundStartDate).toLocaleDateString('pl-PL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </span>
        )}
        {effectiveStartTime && (
          <span className="round-meta__chip">🕒 {formatStartTime(effectiveStartTime)}</span>
        )}
        {venueWithCity && (
          <span className="round-meta__chip">
            📍 {venueWithCity}
            {venueIsOverride && <span className="round-meta__override">(zmiana)</span>}
          </span>
        )}
        {roundSeason && (
          <Link
            to={`/leagues/${id}/standings?seasonId=${roundSeason.id}`}
            className="round-meta__chip round-meta__chip--link"
            data-testid="round-season-link"
          >
            🏆 <span className="round-meta__chip--link__text">{roundSeason.name}</span>
          </Link>
        )}
        {leagueName && (
          <Link
            to={`/leagues/${id}`}
            className="round-meta__chip round-meta__chip--link"
            data-testid="round-league-link"
          >
            🏸 <span className="round-meta__chip--link__text">{leagueName}</span>
          </Link>
        )}
        {roundMeta?.venueNotes && (
          <p className="round-meta__notes">ℹ {roundMeta.venueNotes}</p>
        )}
      </div>

      {roundStateMessage && (
        <p className="round-state-message" data-testid="round-state-message">
          {roundStateMessage}
        </p>
      )}

      <div id="zapisy" />

      {signupBlocked && (
        <aside
          className={`round-signup-blocked round-signup-blocked--${
            signupMode === 'PRIVATE' ? 'private' : 'info'
          }`}
        >
          <span className="round-signup-blocked__icon" aria-hidden="true">
            {signupMode === 'PRIVATE' ? '🔒' : 'ℹ'}
          </span>
          <div className="round-signup-blocked__body">
            <h2 className="round-signup-blocked__title">{signupBlocked.title}</h2>
            <p className="round-signup-blocked__text">{signupBlocked.body}</p>
            {signupBlocked.cta && (
              <Link to={signupBlocked.cta.to} className="round-signup-blocked__cta">
                {signupBlocked.cta.label}
              </Link>
            )}
          </div>
        </aside>
      )}

      {!user && isSignupStage(roundStatus) && (isPublicSignupMode(signupMode) || Boolean(accessCode)) && (
        <section className="round-signup-cta" data-testid="round-signup-guest-section">
          <h2 className="round-signup-cta__title">Zapisz się na kolejkę</h2>
          <p className="round-signup-cta__hint">
            {inviteCodeActive
              ? 'Wybierz osobę z listy zawodników klubu — zapis wchodzi od razu. Możesz zapisać kolejne osoby.'
              : signupMode === 'OPEN'
                ? 'Znajdź siebie na liście zawodników klubu — zapis wchodzi od razu.'
                : 'Znajdź siebie na liście zawodników klubu.'}
          </p>
          {americano && <AmericanoSignupNote />}
          {guestSignedUp.length > 0 && (
            <p className="round-signup-cta__hint" data-testid="guest-signed-up-list">
              Zapisani z tego linku: {guestSignedUp.join(', ')}
            </p>
          )}
          {guestOutcome ? (
            <div className="guest-signup-done">
              <p className="auth-message" data-testid="guest-success-message">
                {guestOutcomeMessage(guestOutcome)}
              </p>
              {inviteCodeActive && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={startAnotherGuestSignup}
                  data-testid="guest-signup-another"
                >
                  Zapisz kolejną osobę
                </button>
              )}
            </div>
          ) : (
            <form
              className="round-signup-cta__form guest-signup-form"
              data-testid="round-signup-guest-form"
              onSubmit={handleGuestSubmit}
              autoComplete="off"
            >
              <div className="form-field">
                <label>Zawodnik</label>
                {guestPlayer ? (
                  <div className="guest-player-selected" data-testid="guest-player-selected">
                    <span className="guest-player-selected__name">{guestPlayer.name}</span>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setGuestPlayer(null)}
                      data-testid="guest-player-clear"
                    >
                      zmień
                    </button>
                  </div>
                ) : (
                  <>
                    <PlayerAutocomplete
                      placeholder="Wpisz swoje imię i nazwisko…"
                      fetchPlayers={fetchPublicPlayers}
                      ratingKind="none"
                      createLabel="Nie ma mnie na liście — zgłoś nowego zawodnika"
                      onSelect={(selection) => {
                        if ('newPlayer' in selection) {
                          setGuestPlayer({ newPlayer: true, name: selection.query });
                        } else {
                          setGuestPlayer({ id: selection.id, name: `${selection.firstName} ${selection.lastName}` });
                        }
                      }}
                    />
                    <NewPlayerInline
                      toggleLabel="Nie ma mnie w bazie? Zgłoś nowego zawodnika"
                      submitLabel="Użyj tych danych"
                      testIdPrefix="guest-new-player"
                      onSubmit={(fullName) => setGuestPlayer({ newPlayer: true, name: fullName })}
                    />
                  </>
                )}
              </div>
              {guestPlayer && (
                <>
                  {guestIsNewPlayer && (
                    <p className="round-signup-cta__hint" data-testid="guest-new-player-note">
                      Tej osoby nie ma jeszcze w bazie — organizator musi ją dodać, zanim zapis wejdzie w życie.
                    </p>
                  )}
                  {isDoublesDiscipline(leagueDiscipline) && (
                    <div className="form-field">
                      <label>Imię i nazwisko partnera</label>
                      {guestPartnerName ? (
                        <div className="guest-player-selected" data-testid="guest-partner-selected">
                          <span className="guest-player-selected__name">{guestPartnerName}</span>
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => {
                              setGuestPartnerName('');
                              setGuestPartnerId(null);
                            }}
                            data-testid="guest-partner-clear"
                          >
                            zmień
                          </button>
                        </div>
                      ) : (
                        <>
                          <PlayerAutocomplete
                            placeholder="Szukaj partnera…"
                            fetchPlayers={fetchPublicPlayers}
                            ratingKind="none"
                            createLabel="Nie ma go na liście — zgłoś nowego zawodnika"
                            onSelect={(selection) => {
                              if ('newPlayer' in selection) {
                                setGuestPartnerName(selection.query);
                                setGuestPartnerId(null);
                              } else {
                                setGuestPartnerName(`${selection.firstName} ${selection.lastName}`);
                                setGuestPartnerId(selection.id);
                              }
                            }}
                          />
                          <NewPlayerInline
                            toggleLabel="Nie ma go w bazie? Zgłoś nowego zawodnika"
                            submitLabel="Użyj tych danych"
                            testIdPrefix="guest-new-partner"
                            onSubmit={(fullName) => {
                              setGuestPartnerName(fullName);
                              setGuestPartnerId(null);
                            }}
                          />
                        </>
                      )}
                    </div>
                  )}
                  <div className="form-field">
                    <label htmlFor="guest-email">{guestContactOptional ? 'Email (opcjonalnie)' : 'Email'}</label>
                    <input
                      id="guest-email"
                      type="email"
                      autoComplete="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="jan@example.com"
                      data-testid="guest-email-input"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="guest-phone">{guestContactOptional ? 'Telefon (opcjonalnie)' : 'Telefon'}</label>
                    <input
                      id="guest-phone"
                      type="tel"
                      autoComplete="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      placeholder="+48 123 456 789"
                      data-testid="guest-phone-input"
                    />
                  </div>
                  {guestContactError && (
                    <p className="auth-error" data-testid="guest-contact-error">{guestContactError}</p>
                  )}
                  <div className="guest-signup-form__actions">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={guestSubmitting}
                      data-testid="guest-submit-button"
                    >
                      {guestSubmitting
                        ? 'Wysyłanie…'
                        : guestIsNewPlayer
                          ? 'Wyślij prośbę'
                          : 'Zapisz się'}
                    </button>
                  </div>
                </>
              )}
            </form>
          )}
        </section>
      )}

      {user && canSignup?.mySignupStatus === 'SIGNED_UP' && (
        <section className="round-signup-cta" data-testid="round-signup-status">
          <h2 className="round-signup-cta__title">Jesteś zapisany na tę kolejkę</h2>
          <p className="round-signup-cta__hint">
            {canSignup.defaultPlayer
              ? `Zapis: ${canSignup.defaultPlayer.firstName} ${canSignup.defaultPlayer.lastName}.`
              : 'Twój zapis jest aktywny.'}
          </p>
          {signupError && <p className="auth-error">{signupError}</p>}
          {signupMessage && <p className="auth-message">{signupMessage}</p>}
          {/* Self-withdraw is only allowed while registration is open; once grouping/play starts only the organizer can remove a participant */}
          {roundStatus === 'REGISTRATION' && (
            <button
              type="button"
              className="btn-secondary"
              data-testid="round-withdraw-button"
              onClick={handleWithdraw}
              disabled={withdrawing}
            >
              {withdrawing ? 'Wycofuję…' : 'Wycofaj zapis'}
            </button>
          )}
          {roundStatus === 'GROUPING' && (
            <p className="round-signup-cta__hint" data-testid="round-withdraw-hint">
              Organizator układa już pary — jeśli nie możesz przyjść, daj mu znać.
            </p>
          )}
          {/* While the round is live the member CAN enter their own results in the match list below, so surface a discoverable CTA instead of the passive contact hint */}
          {roundStatus === 'ACTIVE' && (
            <p className="round-signup-cta__hint" data-testid="round-scoring-hint">
              Kolejka trwa — wyniki wpisuje się przez link od organizatora. Tutaj widzisz je na żywo.
            </p>
          )}
        </section>
      )}

      {user && canSignup?.mySignupStatus === 'DECLINED' && (
        <section className="round-signup-cta" data-testid="round-signup-status">
          <h2 className="round-signup-cta__title">Nie przyjdziesz na tę kolejkę</h2>
          <p className="round-signup-cta__hint">
            Twój status to <strong>Nie przyjdę</strong>. Skontaktuj się z organizatorem, jeśli to pomyłka.
          </p>
          {signupMessage && <p className="auth-message">{signupMessage}</p>}
        </section>
      )}

      {user && canSignup?.mySignupStatus === 'WAITLIST' && (
        <section className="round-signup-cta" data-testid="round-signup-waitlist">
          <h2 className="round-signup-cta__title">Jesteś na liście oczekujących</h2>
          <p className="round-signup-cta__hint">
            Twój zapis czeka na wolne miejsce. Organizator poinformuje Cię, gdy zostaniesz przyjęty.
          </p>
          {signupMessage && <p className="auth-message">{signupMessage}</p>}
        </section>
      )}

      {/* WAITLIST already has a dedicated status section above, so it must not fall back to the signup form */}
      {user && canSignup?.canSignup && canSignup.mySignupStatus !== 'SIGNED_UP' && canSignup.mySignupStatus !== 'WAITLIST' && (
        (() => {
          const isDoubles = isDoublesDiscipline(leagueDiscipline);
          const sameSelected = pairSlot1 && pairSlot2 && pairSlot1.id === pairSlot2.id;

          if (isDoubles) {
            return (
              <section className="round-signup-cta" data-testid="round-signup-form">
                <h2 className="round-signup-cta__title">Zapisz parę na tę kolejkę</h2>
                <p className="round-signup-cta__hint">
                  {canSignup.requiresApproval
                    ? 'Zgłoszenie pary trafi do akceptacji organizatora.'
                    : 'Zapis natychmiastowy.'}
                </p>
                <p className="round-signup-cta__hint round-signup-cta__hint--mode" data-testid="partner-pool-note">
                  <strong>Nie masz partnera?</strong> Zgłoś się do puli szukających niżej na stronie.
                  Zapisani zobaczą Cię na liście i wyślą propozycję — parę zawiązujesz dopiero swoją
                  akceptacją, więc zagrasz z kim chcesz.
                </p>
                <form onSubmit={handlePairSubmit} className="round-signup-cta__form pair-form" autoComplete="off">
                  <div className="pair-form__slots">
                    {[1, 2].map((slotIdx) => {
                      const slot = slotIdx === 1 ? pairSlot1 : pairSlot2;
                      const setSlot = slotIdx === 1 ? setPairSlot1 : setPairSlot2;
                      const isActive = pairActiveSlot === slotIdx && !slot;
                      return (
                        <div
                          key={slotIdx}
                          className={`pair-form__slot${slot ? ' pair-form__slot--filled' : ''}${isActive ? ' pair-form__slot--active' : ''}`}
                        >
                          <span className="pair-form__slot-label">Zawodnik {slotIdx}</span>
                          {slot ? (
                            <div className="pair-form__chip">
                              <span className="round-signup-panel__monogram" aria-hidden="true">
                                {((slot.firstName.charAt(0) || '?') + (slot.lastName.charAt(0) || '')).toUpperCase()}
                              </span>
                              <span className="pair-form__chip-name">
                                {slot.firstName} {slot.lastName}
                              </span>
                              <button
                                type="button"
                                className="pair-form__chip-remove"
                                onClick={() => {
                                  setSlot(null);
                                  setPairActiveSlot(slotIdx as 1 | 2);
                                }}
                                aria-label={`Usuń ${slot.firstName} ${slot.lastName}`}
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="pair-form__slot-empty"
                              onClick={() => setPairActiveSlot(slotIdx as 1 | 2)}
                            >
                              {isActive ? 'Wpisz nazwisko poniżej…' : '+ Wybierz zawodnika'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {(!pairSlot1 || !pairSlot2) && (
                    <PlayerAutocomplete
                      placeholder={`Szukaj zawodnika do slotu ${pairActiveSlot}…`}
                      ratingKind="doubles"
                      allowCreate={false}
                      onSelect={(picked) => {
                        if ('newPlayer' in picked) {
                          return;
                        }
                        const takenInOther =
                          (pairActiveSlot === 1 && pairSlot2?.id === picked.id) ||
                          (pairActiveSlot === 2 && pairSlot1?.id === picked.id);
                        if (takenInOther) {
                          return;
                        }
                        if (pairActiveSlot === 1) {
                          setPairSlot1(picked);
                          if (!pairSlot2) setPairActiveSlot(2);
                        } else {
                          setPairSlot2(picked);
                          if (!pairSlot1) setPairActiveSlot(1);
                        }
                      }}
                    />
                  )}

                  {sameSelected && (
                    <p className="auth-error">Wybierz dwóch różnych zawodników.</p>
                  )}
                  {signupError && <p className="auth-error">{signupError}</p>}
                  {signupMessage && <p className="auth-message">{signupMessage}</p>}
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={signupSubmitting || !pairSlot1 || !pairSlot2 || !!sameSelected}
                  >
                    {signupSubmitting ? 'Zapisywanie…' : 'Zapisz parę'}
                  </button>
                </form>
              </section>
            );
          }

          return (
            <section className="round-signup-cta" data-testid="round-signup-form">
              <h2 className="round-signup-cta__title">Zapisz się na tę kolejkę</h2>
              <p className="round-signup-cta__hint">
                {canSignup.requiresApproval
                  ? 'Twoje zgłoszenie trafi do akceptacji organizatora.'
                  : 'Zapis natychmiastowy.'}
              </p>
              {americano && <AmericanoSignupNote />}
              <form onSubmit={handleSignupSubmit} className="round-signup-cta__form" autoComplete="off">
                {signupSelected ? (
                  <div className="pair-form__slot pair-form__slot--filled">
                    <span className="pair-form__slot-label">Wybrany zawodnik</span>
                    <div className="pair-form__chip">
                      <span className="round-signup-panel__monogram" aria-hidden="true">
                        {((signupSelected.firstName.charAt(0) || '?') + (signupSelected.lastName.charAt(0) || '')).toUpperCase()}
                      </span>
                      <span className="pair-form__chip-name">
                        {signupSelected.firstName} {signupSelected.lastName}
                      </span>
                      <button
                        type="button"
                        className="pair-form__chip-remove"
                        onClick={() => setSignupSelected(null)}
                        aria-label="Wyczyść wybór"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ) : (
                  <PlayerAutocomplete
                    placeholder="Wpisz imię i nazwisko zawodnika…"
                    ratingKind={americano ? 'doubles' : 'singles'}
                    allowCreate={false}
                    onSelect={(picked) => {
                      if ('newPlayer' in picked) {
                        return;
                      }
                      setSignupSelected(picked);
                    }}
                  />
                )}
                {signupError && <p className="auth-error">{signupError}</p>}
                {signupMessage && <p className="auth-message">{signupMessage}</p>}
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="round-signup-submit"
                  disabled={signupSubmitting || !signupSelected}
                >
                  {signupSubmitting ? 'Zapisywanie…' : 'Zapisz'}
                </button>
              </form>
            </section>
          );
        })()
      )}

      {id && roundId && (
        <PartnerSearchPanel
          leagueId={id}
          roundId={roundId}
          accessCode={accessCode}
          isDoubles={isDoublesDiscipline(leagueDiscipline)}
          isLoggedIn={!!user}
          onPairFormed={async () => {
            await fetchParticipants();
            setCanSignup((prev) => (prev ? { ...prev, mySignupStatus: 'SIGNED_UP' } : prev));
          }}
        />
      )}

      {(() => {
        const signupsActive = isSignupStage(roundStatus) || canSignup?.canSignup;
        // Under way the desk is shut, but who came still matters — the list stays,
        // the queues and seat alerts around it do not.
        const roundUnderWay = roundStatus === 'ACTIVE';
        const showParticipantsBlock = roundUnderWay
          ? signedParticipants.length > 0
          : signupsStillMatter(roundStatus) &&
            (visibleParticipants.length > 0 || signupsActive || participantsError);
        const participantRatingTag = isDoublesDiscipline(ratingDiscipline) ? 'D' : 'S';

        if (!showParticipantsBlock) {
          return null;
        }

        return (
          <section className="round-signup-panel" data-testid="round-participants-panel">
            {participantsError && participants === null && (
              <p className="round-signup-panel__error" data-testid="participants-error">
                Nie udało się załadować listy uczestników. Spróbuj odświeżyć stronę.
              </p>
            )}
            {seatsFull && !roundUnderWay && (
              <p className="round-signup-panel__seats-alert" data-testid="round-seats-alert">
                {seats!.isOverLimit
                  ? `Limit ${seats!.maxParticipants} miejsc przekroczony — organizator dopisał ${seats!.signedUpCount} osób.`
                  : `Brak wolnych miejsc (${seats!.signedUpCount}/${seats!.maxParticipants}) — kolejne zapisy trafiają na listę oczekujących.`}
              </p>
            )}

            {!roundUnderWay && (
              <>
                <WaitingGroup
                  testId="round-participants-waitlist"
                  countTestId="participants-waitlist-count"
                  title="Do zatwierdzenia"
                  hint="Zgłoszenia czekają na decyzję organizatora."
                  participants={approvalParticipants}
                  emptyText="Nikt nie czeka na decyzję."
                  relevant={roundNeedsApproval}
                  ratingTag={participantRatingTag}
                />

                <WaitingGroup
                  testId="round-participants-seat-queue"
                  countTestId="participants-seat-queue-count"
                  title="Lista oczekujących"
                  hint="Wejdą do gry, gdy zwolni się miejsce albo organizator zdecyduje inaczej."
                  participants={seatQueueParticipants}
                  emptyText="Nikt nie czeka na zwolnione miejsce."
                  relevant={roundHasSeatLimit}
                  ratingTag={participantRatingTag}
                />
              </>
            )}

            <div className="round-signup-panel__group">
              <div className="round-signup-panel__group-head">
                <h3 className="round-signup-panel__group-title">
                  Zapisani
                  <span
                    className={`round-signup-panel__count${seatsFull ? ' round-signup-panel__count--warn' : ''}`}
                    data-testid="participants-signed-count"
                  >
                    {seats?.maxParticipants != null
                      ? `${signedParticipants.length} / ${seats.maxParticipants}`
                      : signedParticipants.length}
                  </span>
                </h3>
                {signedParticipants.length > 1 && (
                  <div className="signed-sort" role="group" aria-label="Sortowanie listy zapisanych">
                    <button
                      type="button"
                      className={`signed-sort__btn${signedSortMode === 'rating-desc' ? ' is-active' : ''}`}
                      onClick={() => setSignedSortMode('rating-desc')}
                      aria-pressed={signedSortMode === 'rating-desc'}
                    >
                      Ranking ↓
                    </button>
                    <button
                      type="button"
                      className={`signed-sort__btn${signedSortMode === 'name-asc' ? ' is-active' : ''}`}
                      onClick={() => setSignedSortMode('name-asc')}
                      aria-pressed={signedSortMode === 'name-asc'}
                    >
                      A→Z
                    </button>
                  </div>
                )}
              </div>
              {signedParticipants.length === 0 ? (
                <p className="round-signup-panel__empty">
                  {signupsActive
                    ? 'Nikt nie zapisał się jeszcze — bądź pierwszy.'
                    : 'Nikt nie zgłosił udziału.'}
                </p>
              ) : (
                <ol className="round-signup-panel__list">
                  {[...signedParticipants]
                    .sort((a, b) => {
                      if (signedSortMode === 'name-asc') {
                        return a.name.localeCompare(b.name, 'pl');
                      }
                      const ratingA = ratingForTag(a, participantRatingTag) ?? -Infinity;
                      const ratingB = ratingForTag(b, participantRatingTag) ?? -Infinity;

                      if (ratingA !== ratingB) {
                        return ratingB - ratingA;
                      }

                      return a.name.localeCompare(b.name, 'pl');
                    })
                    .map((p, i) => (
                      <li key={p.leaguePlayerId} className="round-signup-panel__row">
                        <span className="round-signup-panel__index">{i + 1}</span>
                        <span className="round-signup-panel__monogram" aria-hidden="true">
                          {participantInitials(p.name)}
                        </span>
                        <span className="round-signup-panel__name">
                          {p.name}
                          {p.groupName && (
                            <span className="round-signup-panel__group-tag"> · Grupa {p.groupName}</span>
                          )}
                        </span>
                        <span className="round-signup-panel__rating">
                          <RatingChip
                            tag={participantRatingTag}
                            value={ratingForTag(p, participantRatingTag)}
                            pair={p.partnerPlayerId != null}
                            prominent
                          />
                        </span>
                      </li>
                    ))}
                </ol>
              )}
            </div>
          </section>
        );
      })()}

      {!embargoed && roundStatus === 'ACTIVE' && (
        <p className="round-preview-note" data-testid="round-preview-note">
          Wyniki są podglądowe — ranking zaktualizuje się po zamknięciu kolejki
        </p>
      )}

      {showScoringPanel && scoring && (
        <RoundScoringPanel
          groups={scoringGroups}
          matches={scoring.matches}
          live={roundStatus === 'ACTIVE'}
          runUnit={americano ? 'runda' : 'kółko'}
        />
      )}

      {tournamentGroups.length > 0 && (
        <div className="round-tournament-groups">
          {tournamentGroups.map((group) => (
            <TournamentResultsView
              key={group.tournamentNumber}
              tournamentNumber={group.tournamentNumber}
              title={group.groupName ?? undefined}
              leagueRoundId={roundId ? Number(roundId) : undefined}
              showGroupStandings={!americano}
            />
          ))}
        </div>
      )}

      {embargoed ? (
        <div className="round-embargo" data-testid="round-embargo-message">
          <p className="round-embargo__title">
            Wyniki kolejki zostaną opublikowane przez organizatora
          </p>
          <p className="round-embargo__note">
            Twoje wpisanie zostało zapisane. Tabela ligi aktualizuje się automatycznie, ale lista meczów jest widoczna dopiero po zamknięciu kolejki.
          </p>
        </div>
      ) : showScoringPanel ? null : tournamentGroups.length === 0 && matches.length === 0 ? (
        roundStatus === 'ACTIVE' || roundStatus === 'CLOSED' ? (
          <p data-testid="round-match-empty">Brak rozegranych meczów w tej kolejce.</p>
        ) : null
      ) : tournamentGroups.length === 0 &&
        (roundStatus === 'ACTIVE' || roundStatus === 'CLOSED') ? (
        <ul className="round-match-list" data-testid="round-match-list">
          {matches.map((match) => {
            const p1Wins = match.winnerSide === 'SIDE1' || match.winnerSide === 'WALKOVER_P1';
            const p2Wins = match.winnerSide === 'SIDE2' || match.winnerSide === 'WALKOVER_P2';
            const isWalkover = match.winnerSide?.startsWith('WALKOVER') ?? false;
            const hasResult = match.winnerSide !== null;

            const side1 = match.side1 ?? [{ name: match.player1Name, ratingBefore: match.ratingBefore1 }];
            const side2 = match.side2 ?? [{ name: match.player2Name, ratingBefore: match.ratingBefore2 }];

            return (
              <li key={match.id} data-testid={`match-row-${match.id}`} className={`round-match-row${!hasResult ? ' round-match-row--pending' : ''}`}>
                <div className="match-row-header">
                  <span className={`match-player${p1Wins ? ' match-player--winner' : ''}`}>
                    {side1.map((player, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 && <span className="match-player-separator"> / </span>}
                        {player.name}
                        {player.ratingBefore != null && (
                          <span className="match-player-rating" title="Ranking przed meczem">
                            RTG {player.ratingBefore}
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </span>
                  <span className={`match-sets${isWalkover ? ' match-sets--walkover' : ''}`}>
                    {formatMatchSets(match.sets, match.winnerSide)}
                  </span>
                  <span className={`match-player match-player--right${p2Wins ? ' match-player--winner' : ''}`}>
                    {side2.map((player, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 && <span className="match-player-separator"> / </span>}
                        {player.ratingBefore != null && (
                          <span className="match-player-rating" title="Ranking przed meczem">
                            RTG {player.ratingBefore}
                          </span>
                        )}
                        {player.name}
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default RoundDetail;
